import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  NgZone,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ChatActionsService, TwitchMessage } from '../services/combined-chat.service';
import { LiveChatScrollController } from './live-chat-scroll';

interface TestConfig {
  rate: number;
  burst: number;
  burstEveryMs: number;
}

@Component({
  selector: 'app-horizontal-messages',
  standalone: true,
  templateUrl: './horizontal-messages.component.html',
  styleUrls: ['./horizontal-messages.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HorizontalMessagesComponent implements AfterViewInit, OnDestroy {
  private readonly chatService = inject(ChatActionsService);
  private readonly zone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);
  private readonly session = 1;
  private readonly test = this.readTestConfig();

  /** Guaranteed backstop for the ~24-30h chat-freeze. The WS message-liveness
   *  watchdog self-heals the "backend stopped pushing to this socket" case in
   *  minutes; this full reload additionally clears anything render-side we
   *  haven't pinned down (stalled marquee, id-dedup drift, memory). 24h sits at
   *  the low end of the observed freeze window, so it pre-empts it. */
  private readonly dailyReloadMs = 24 * 60 * 60 * 1000;
  private reloadTimer: ReturnType<typeof setTimeout> | null = null;

  @ViewChild('viewport') viewport!: ElementRef<HTMLDivElement>;
  @ViewChild('messagesWrapper') messagesWrapper!: ElementRef<HTMLDivElement>;

  readonly displayMessages = this.chatService.getAlerts(this.session);
  private scrollController?: LiveChatScrollController;

  constructor() {
    if (this.test) {
      this.chatService.startTestMode(this.session, this.test);
    } else {
      this.chatService.startAlertsPolling(this.session);
      this.scheduleDailyReload();
    }
  }

  private scheduleDailyReload(): void {
    if (typeof window === 'undefined') return;
    this.reloadTimer = setTimeout(() => window.location.reload(), this.dailyReloadMs);
  }

  ngAfterViewInit(): void {
    if (!this.test) {
      this.chatService.ensureSessionConnected(this.session);
    }
    this.scrollController = new LiveChatScrollController(
      this.zone,
      () => this.messagesWrapper?.nativeElement,
      () => this.viewport?.nativeElement,
      () => this.chatService.trimLeadingMessage(this.session),
      () => this.cdr.detectChanges(),
      (head: number) => this.chatService.keepHeadAndRecentHistory(this.session, head),
    );
    this.scrollController.start();
  }

  ngOnDestroy(): void {
    this.scrollController?.stop();
    if (this.reloadTimer !== null) {
      clearTimeout(this.reloadTimer);
      this.reloadTimer = null;
    }
    if (this.test) {
      this.chatService.stopTestMode(this.session);
    }
  }

  trackByMessageId(index: number, message: TwitchMessage): string {
    return String(message.trackId ?? message.id ?? index);
  }

  /** `?testMode=1&rate=<msgs/sec>&burst=<N>&burstEvery=<ms>` → synthetic feed. */
  private readTestConfig(): TestConfig | null {
    const q = this.route.snapshot.queryParamMap;
    const on = q.get('testMode');
    if (!on || on === '0' || on === 'false') return null;
    return {
      rate: Math.max(0, Number(q.get('rate')) || 2),
      burst: Math.max(0, Number(q.get('burst')) || 0),
      burstEveryMs: Math.max(1000, Number(q.get('burstEvery')) || 12000),
    };
  }
}
